import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDownTrayIcon, StopIcon, PlayIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { usePageNameContext } from "../../../contexts/PageTitleContext";
import { Button } from "../../../components/base/Button";
import { TextField } from "../../../components/base/TextField";
import LoadingWheel from "../../../components/shared/LoadingWheel";
import useNotification from "../../../components/shared/Notifications/useNotifications";
import useSellerHistory from "../../../data/SellerHistory/useSellerHistory";
import useSellerHistoryScrape from "../../../data/SellerHistory/useSellerHistoryScrape";
import { SellerHistoryQueryKeys } from "../../../data/SellerHistory/keys";
import { ISellerSoldHistory } from "../../../models/SellerHistory/SellerSoldHistory";
import PartDetailsModal from "./PartDetailsModal";
import { compatibilityToText, itemSpecificsToText } from "./partDetails";

interface FormFields {
  seller: string;
  startPage?: number;
  maxPages?: number;
}

interface Progress {
  running: boolean;
  currentPage: number;
  totalPages?: number;
  pagesProcessed: number;
  newItemsStored: number;
  skippedExisting: number;
  message: string;
}

const initialProgress: Progress = {
  running: false,
  currentPage: 0,
  totalPages: undefined,
  pagesProcessed: 0,
  newItemsStored: 0,
  skippedExisting: 0,
  message: "",
};

export default function SellerHistory() {
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();

  const { register, handleSubmit, getValues } = useForm<FormFields>({
    defaultValues: { seller: "", startPage: undefined, maxPages: undefined },
  });

  const { scrapePage } = useSellerHistoryScrape();

  const [activeSeller, setActiveSeller] = useState("");
  const [progress, setProgress] = useState<Progress>(initialProgress);
  // Results are only fetched/shown once the user explicitly loads them.
  const [showResults, setShowResults] = useState(false);
  // Part whose specifics / compatibility are shown in the details modal.
  const [detailPart, setDetailPart] = useState<ISellerSoldHistory | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Ref flag lets the Stop button break the async scrape loop.
  const stopRequested = useRef(false);

  const { history } = useSellerHistory(activeSeller, showResults);

  const DISPLAY_LIMIT = 100;

  useEffect(() => {
    setInfo({
      name: "Seller Sold History",
      desc: "Scrape an eBay seller's sold listings and export them as a CSV.",
      pageTitle: "Seller History",
    });
  }, []);

  const runScrape = async (values: FormFields) => {
    const seller = (values.seller || "").trim();
    if (!seller) {
      addNotification({ variant: "error", primaryText: "Enter a seller name first" });
      return;
    }

    // Blank start page -> begin at 1. Blank max pages -> scrape every page until
    // eBay reports no more (or the user hits Stop).
    const startRaw = values.startPage;
    const maxRaw = values.maxPages;

    const startPage =
      typeof startRaw === "number" && Number.isFinite(startRaw) && startRaw > 0
        ? Math.floor(startRaw)
        : 1;

    let endPage = Infinity;
    if (typeof maxRaw === "number" && Number.isFinite(maxRaw) && maxRaw > 0) {
      endPage = startPage + Math.floor(maxRaw) - 1;
    }

    stopRequested.current = false;
    setActiveSeller(seller);
    setProgress({ ...initialProgress, running: true, message: "Starting…" });

    let pagesProcessed = 0;
    let newItemsStored = 0;
    let skippedExisting = 0;

    try {
      for (let page = startPage; page <= endPage; page++) {
        if (stopRequested.current) break;

        setProgress((p) => ({ ...p, currentPage: page, message: `Scraping page ${page}…` }));

        const result = await scrapePage.mutateAsync({ seller, page });
        if (!result) break;

        pagesProcessed++;
        newItemsStored += result.newItemsStored;
        skippedExisting += result.skippedExisting;

        setProgress((p) => ({
          ...p,
          totalPages: result.totalPages ?? p.totalPages,
          pagesProcessed,
          newItemsStored,
          skippedExisting,
          message: `Page ${page}: +${result.newItemsStored} new, ${result.skippedExisting} skipped`,
        }));

        if (!result.hasMorePages) {
          setProgress((p) => ({ ...p, message: `${p.message} — no more pages` }));
          break;
        }
      }

      addNotification({
        variant: "success",
        primaryText: `Scrape finished: ${newItemsStored} new item(s) stored`,
      });
    } catch (err: any) {
      console.error(err);
      addNotification({
        variant: "error",
        primaryText: err?.message || "Scrape failed",
      });
    } finally {
      setProgress((p) => ({
        ...p,
        running: false,
        message: stopRequested.current ? "Stopped" : "Done",
      }));
      queryClient.invalidateQueries({
        queryKey: SellerHistoryQueryKeys.forSeller(seller.toLowerCase()),
      });
    }
  };

  const handleStop = () => {
    stopRequested.current = true;
    setProgress((p) => ({ ...p, message: "Stopping after current page…" }));
  };

  const loadResults = () => {
    const seller = (getValues("seller") || activeSeller || "").trim();
    if (!seller) {
      addNotification({ variant: "error", primaryText: "Enter a seller name first" });
      return;
    }
    setActiveSeller(seller);
    setShowResults(true);
  };

  const exportCsv = () => {
    const rows = history.data || [];
    if (rows.length === 0) {
      addNotification({ variant: "error", primaryText: "Nothing to export yet" });
      return;
    }

    const headers = [
      "productId",
      "title",
      "condition",
      "price",
      "priceValue",
      "currency",
      "soldCount",
      "soldDate",
      "lastRevised",
      "categories",
      "itemSpecifics",
      "compatibility",
      "link",
      "dateStored",
    ];

    const escape = (value: unknown) => {
      const str = value === undefined || value === null ? "" : String(value);
      return `"${str.replace(/"/g, '""')}"`;
    };

    const lines = [headers.join(",")];
    rows.forEach((r: ISellerSoldHistory) => {
      lines.push(
        [
          r.productId,
          r.title,
          r.condition,
          r.priceRaw,
          r.priceExtracted,
          r.currency,
          r.soldCount,
          r.soldDate,
          r.lastRevised,
          r.categories,
          itemSpecificsToText(r.itemSpecifics),
          compatibilityToText(r.compatibility),
          r.link,
          r.created,
        ]
          .map(escape)
          .join(",")
      );
    });

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = `${activeSeller || "seller"}-sold-history.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const rows = history.data || [];
  const displayRows = rows.slice(0, DISPLAY_LIMIT);

  return (
    <div className="flex flex-col space-y-6">
      {/* Scrape controls */}
      <form
        onSubmit={handleSubmit(runScrape)}
        className="bg-white rounded shadow p-6 flex flex-col space-y-4"
      >
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-grow min-w-[240px]">
            <TextField
              label="eBay seller name"
              placeholder="e.g. charlestrentlimited"
              fullWidth
              disabled={progress.running}
              {...register("seller", { required: true })}
            />
          </div>
          <div className="w-32">
            <TextField
              label="Start page"
              type="number"
              min={1}
              placeholder="1"
              fullWidth
              disabled={progress.running}
              {...register("startPage", { valueAsNumber: true })}
            />
          </div>
          <div className="w-32">
            <TextField
              label="Max pages"
              type="number"
              min={1}
              placeholder="All"
              fullWidth
              disabled={progress.running}
              {...register("maxPages", { valueAsNumber: true })}
            />
          </div>

          {!progress.running ? (
            <Button type="submit" className="flex items-center space-x-2">
              <PlayIcon className="h-5 w-5 -ml-1" />
              <span>Start scrape</span>
            </Button>
          ) : (
            <Button
              type="button"
              variant="outlined"
              onClick={handleStop}
              className="flex items-center space-x-2"
            >
              <StopIcon className="h-5 w-5 -ml-1" />
              <span>Stop</span>
            </Button>
          )}
        </div>

        <p className="text-xs text-gray-500">
          Each page runs a product search per new listing (one SerpApi credit each). Already-stored
          products are skipped, so re-runs only fetch new items.
        </p>

        {(progress.running || progress.pagesProcessed > 0) && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm border-t pt-3">
            {progress.running && <LoadingWheel />}
            <span>
              Page <b>{progress.currentPage}</b>
              {progress.totalPages ? ` / ${progress.totalPages}` : ""}
            </span>
            <span>Pages processed: <b>{progress.pagesProcessed}</b></span>
            <span className="text-green-700">New stored: <b>{progress.newItemsStored}</b></span>
            <span className="text-gray-600">Skipped: <b>{progress.skippedExisting}</b></span>
            <span className="text-gray-500 italic">{progress.message}</span>
          </div>
        )}
      </form>

      {/* Results */}
      <div className="bg-white rounded shadow p-6 flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Stored sold products {activeSeller ? `for "${activeSeller}"` : ""}
            {showResults && rows.length > 0 ? ` (${rows.length})` : ""}
          </h2>
          <div className="flex items-center space-x-3">
            <Button
              type="button"
              variant="outlined"
              onClick={loadResults}
              className="flex items-center space-x-2"
            >
              <MagnifyingGlassIcon className="h-5 w-5 -ml-1" />
              <span>{showResults ? "Refresh" : "Load results"}</span>
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={exportCsv}
              disabled={!showResults || rows.length === 0}
              className="flex items-center space-x-2"
            >
              <ArrowDownTrayIcon className="h-5 w-5 -ml-1" />
              <span>Export CSV</span>
            </Button>
          </div>
        </div>

        {!showResults ? (
          <p className="text-sm text-gray-500 py-4">
            Click <b>Load results</b> to view the stored sold products for this seller.
          </p>
        ) : history.isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingWheel />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">
            No stored products yet for this seller. Run a scrape above.
          </p>
        ) : (
          <div className="overflow-x-auto">
            {rows.length > DISPLAY_LIMIT && (
              <p className="text-xs text-gray-500 mb-2">
                Showing first {DISPLAY_LIMIT} of {rows.length}. Export CSV for the full list.
              </p>
            )}
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b text-gray-600">
                  <th className="py-2 pr-4">Image</th>
                  <th className="py-2 pr-4">Title</th>
                  <th className="py-2 pr-4">Condition</th>
                  <th className="py-2 pr-4">Price</th>
                  <th className="py-2 pr-4">Sold</th>
                  <th className="py-2 pr-4">Sold date</th>
                  <th className="py-2 pr-4">Details</th>
                  <th className="py-2 pr-4">Product ID</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((r: ISellerSoldHistory) => (
                  <tr key={r.id} className="border-b hover:bg-gray-50">
                    <td className="py-2 pr-4">
                      {r.thumbnail ? (
                        <img src={r.thumbnail} alt="" className="h-10 w-10 object-cover rounded" />
                      ) : null}
                    </td>
                    <td className="py-2 pr-4 max-w-md">
                      {r.link ? (
                        <a
                          href={r.link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-orange-600 hover:underline line-clamp-2"
                          title={r.title}
                        >
                          {r.title}
                        </a>
                      ) : (
                        <span className="line-clamp-2" title={r.title}>{r.title}</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap">{r.condition}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">{r.priceRaw}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">{r.soldCount ?? ""}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">{r.soldDate}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {(() => {
                        const specCount = r.itemSpecifics?.length ?? 0;
                        const compatCount = r.compatibility?.items?.length ?? 0;
                        if (specCount === 0 && compatCount === 0) {
                          return <span className="text-gray-400">—</span>;
                        }
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              setDetailPart(r);
                              setDetailOpen(true);
                            }}
                            className="text-orange-600 hover:underline"
                          >
                            {specCount} spec{specCount === 1 ? "" : "s"}
                            {" · "}
                            {compatCount} vehicle{compatCount === 1 ? "" : "s"}
                          </button>
                        );
                      })()}
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap text-gray-500">{r.productId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PartDetailsModal open={detailOpen} setOpen={setDetailOpen} part={detailPart} />
    </div>
  );
}
